import React, { useState, useEffect, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import { FaTrash } from "react-icons/fa";
import Placeholder from "../../assets/images/placeholder.png";
import DeleteIcon from "../../assets/images/icons/delete-w.svg";
import { Link } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../utils/constants";
import editImage from "../../assets/images/icons/edit-2.svg";
import viewIcon from "../../assets/images/icons/eye.svg";
import Modal from "../../commons/Modal/Modal";

const getMediaUrl = (path) => {
  if (!path || typeof path !== "string") return path || null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = (process.env.REACT_APP_API || "http://quytech.in:5001").replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

function BannerManagement() {
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logout, showLogout] = useState(false);
  const [logoutModal, setLogoutModal] = useState({ value: false });
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const getBanners = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append("search", searchTerm);
      }

      const response = await handleApiCall("get", `${endpoints.bannerList}${params.toString() ? `?${params.toString()}` : ""}`);
      if (response?.data?.code === 200 || response?.data?.code === "200" || response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const bannersData = response?.data?.data?.data || response?.data?.data || [];
        setBanners(bannersData);
        setTotalPages(response?.data?.data?.pagination?.totalPages || response?.data?.pagination?.totalPages || 1);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching banners:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    getBanners();
  }, [getBanners]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusToggle = async (bannerId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;

      const response = await handleApiCall("patch", `${endpoints.updatestatus}/${bannerId}`, {
        status: newStatus,
      });

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(UI_CONSTANTS.TOAST_MESSAGES.SUCCESS.STATUS_UPDATED);
        getBanners(); // Refresh the list
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error updating banner status:", error);
      // Network errors are already handled by handleApiCall
    }
  };

  const handleDeleteClick = (bannerId) => {
    setBannerToDelete(bannerId);
    setLogoutModal({ value: true });
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;

    setDeleting(true);
    try {
      const response = await handleApiCall("delete", `${endpoints.deleteBanner}/${bannerToDelete}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(UI_CONSTANTS.TOAST_MESSAGES.SUCCESS.BANNER_DELETED);
        setLogoutModal({ value: false });
        setBannerToDelete(null);
        getBanners(); // Refresh the list
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error deleting banner:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setLogoutModal({ value: false });
    setBannerToDelete(null);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">{UI_CONSTANTS.PAGE_TITLES.BANNER_MANAGEMENT}</h3>
              {/* <Link to="/add-banner" className="btn btn-primary rounded-2 px-3 ml-auto">
                {UI_CONSTANTS.BUTTON_LABELS.CREATE_NEW} <span className="ml-2">+</span>
              </Link> */}
            </div>

            {/* Search Bar */}
            {/* <div className="row mb-3">
              <div className="col-md-6">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control search-icon left-icon border"
                    placeholder={UI_CONSTANTS.PLACEHOLDERS.SEARCH}
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
            </div> */}

            <div className="table-responsive px-0">
              <table className="table table-striped trim text-center align-middle">
                <thead>
                  <tr>
                    <th width="80">{UI_CONSTANTS.TABLE_HEADERS.SR_NO}</th>
                    <th width="150">{UI_CONSTANTS.TABLE_HEADERS.NAME}</th>
                    <th>{UI_CONSTANTS.TABLE_HEADERS.IMAGE}</th>
                    {/* <th>{UI_CONSTANTS.TABLE_HEADERS.STATUS}</th> */}
                    <th className="text-center">{UI_CONSTANTS.TABLE_HEADERS.ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
                        </div>
                      </td>
                    </tr>
                  ) : banners.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        {/* {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND} */}
                        {""}
                      </td>
                    </tr>
                  ) : (
                    banners.map((banner, index) => (
                      <tr key={banner._id}>
                        <td className="align-middle">{(currentPage - 1) * 10 + index + 1}</td>
                        {/* <td className="align-middle">{banner.title_english || banner.title || UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}</td> */}
                        <td className="align-middle">{banner.title_english || banner.title || ""}</td>
                        <td className="align-middle">
                          <div className="sm-img d-flex justify-content-center align-items-center mx-auto" style={{ width: "60px", height: "60px" }}>
                            <img
                              src={getMediaUrl(banner.desktop_image || banner.file || banner.image) || Placeholder}
                              alt={UI_CONSTANTS.TABLE_HEADERS.IMAGE}
                              style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "6px" }}
                              onError={(e) => {
                                e.target.src = Placeholder;
                              }}
                            />
                          </div>
                        </td>
                        {/* <td>
                          <label className="custom-control custom-switch p-1 ml-4 mb-0 mt-1" role="button">
                            <input type="checkbox" className="custom-control-input" checked={banner.status === 1} onChange={() => handleStatusToggle(banner._id, banner.status)} />
                            <span className="custom-control-label custom-toggle" />
                          </label>
                        </td> */}
                        <td className="align-middle">
                          <div className="d-flex align-items-center justify-content-center">
                            <Link to={`/view-banner/${banner._id}`} className="btn btn-link p-0 mx-2" title={UI_CONSTANTS.BUTTON_LABELS.VIEW || "View"}>
                              <img src={viewIcon} alt={UI_CONSTANTS.BUTTON_LABELS.VIEW || "View"} style={{ width: 20, height: 20, filter: "brightness(0) invert(1)", opacity: 0.95 }} className="mr-2" />
                            </Link>
                            <Link to={`/edit-banner/${banner._id}`} className="btn btn-link p-0 mx-2">
                              <img src={editImage} alt={UI_CONSTANTS.BUTTON_LABELS.EDIT} className="mr-2" />
                            </Link>
                            {/* <button className="btn btn-link p-0 mx-2" onClick={() => handleDeleteClick(banner._id)}>
                              <img src={DeleteIcon} alt={UI_CONSTANTS.BUTTON_LABELS.DELETE} />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Page navigation">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        {UI_CONSTANTS.BUTTON_LABELS.PREVIOUS || "Previous"}
                      </button>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(page)}>
                          {page}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        {UI_CONSTANTS.BUTTON_LABELS.NEXT || "Next"}
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal show={logoutModal.value} onClose={handleDeleteCancel} style={{ zIndex: "1000" }} className=" shadow-none">
        <div className="modal-dialog modal-md modal-center" role="document">
          <div className="modal-content bg-black border-light">
            <div className="modal-body text-center my-4 py-4">
              <h4 className="text-white">
                Do you want to
                <br />
                Delete this banner?
              </h4>
              <div className="col-sm-12 mx-auto d-flex align-items-center mt-4 row">
                <div className="col-6 px-2">
                  <button type="button" className="btn btn-primary btn-lg btn-block font-16 rounded-pill" onClick={handleDeleteConfirm} disabled={deleting}>
                    {deleting ? "Deleting..." : "Yes"}
                  </button>
                </div>
                <div className="col-6 px-2">
                  <button type="button" className="btn btn-default btn-lg btn-block font-16 rounded-pill" onClick={handleDeleteCancel} disabled={deleting}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BannerManagement;
